import { useEffect, useMemo, useRef, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import {
  addFoodEntry,
  buildFoodKey,
  calcMacrosForServing,
  createCustomFood,
  deleteCustomFood,
  deleteFoodEntry,
  getEffectiveMacroTargets,
  getFoodDetails,
  getCustomFoods,
  getFavoriteFoods,
  getFoodLog,
  getMacroLogRange,
  getMacroTargets,
  getRecentFoods,
  parseNutritionLabel,
  removeFavoriteFood,
  saveDailyTargetOverride,
  saveFavoriteFood,
  saveMacroTargets,
  saveRecentFood,
  searchFoodByBarcode,
  searchFoodByName,
} from "./macroTrackerLib";

const MEAL_LABELS = ["Breakfast", "Lunch", "Dinner", "Snacks"];

const cardStyle = {
  background: "#f8fbff",
  borderRadius: "18px",
  padding: "18px",
  border: "2px solid #bfdbfe",
  marginBottom: "14px",
};

const inputStyle = {
  width: "100%",
  padding: "11px 14px",
  borderRadius: "12px",
  border: "2px solid #bfdbfe",
  background: "#f5faff",
  color: "#1e3a5f",
  fontSize: "15px",
  boxSizing: "border-box",
  outline: "none",
};

const btnPrimary = {
  padding: "11px 18px",
  borderRadius: "12px",
  border: "none",
  background: "#38bdf8",
  color: "#fff",
  fontWeight: "bold",
  fontSize: "14px",
  cursor: "pointer",
};

const btnSecondary = {
  padding: "11px 18px",
  borderRadius: "12px",
  border: "2px solid #bfdbfe",
  background: "#f5faff",
  color: "#1e3a5f",
  fontWeight: "bold",
  fontSize: "14px",
  cursor: "pointer",
};

function MacroBar({ label, value, target, color }) {
  const pct = target > 0 ? Math.min(100, Math.round((value / target) * 100)) : 0;
  return (
    <div style={{ marginBottom: "10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
        <span style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "13px" }}>{label}</span>
        <span style={{ color: "#4b6b8a", fontSize: "13px" }}>
          {value}g / {target}g
        </span>
      </div>
      <div style={{ height: "10px", background: "#dbeafe", borderRadius: "999px", overflow: "hidden" }}>
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: color,
            borderRadius: "999px",
            transition: "width 0.3s ease",
          }}
        />
      </div>
    </div>
  );
}

function CalorieRing({ value, target }) {
  const pct = target > 0 ? Math.min(1, value / target) : 0;
  const r = 44;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;
  const over = value > target;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r={r} fill="none" stroke="#dbeafe" strokeWidth="10" />
        <circle
          cx="55"
          cy="55"
          r={r}
          fill="none"
          stroke={over ? "#f87171" : "#38bdf8"}
          strokeWidth="10"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 55 55)"
          style={{ transition: "stroke-dasharray 0.4s ease" }}
        />
        <text x="55" y="50" textAnchor="middle" fill="#1e3a5f" fontSize="18" fontWeight="bold">
          {value}
        </text>
        <text x="55" y="66" textAnchor="middle" fill="#4b6b8a" fontSize="11">
          / {target} kcal
        </text>
      </svg>
      <span style={{ color: over ? "#ef4444" : "#4b6b8a", fontSize: "12px", fontWeight: "bold" }}>
        {over ? `${value - target} over` : `${target - value} remaining`}
      </span>
    </div>
  );
}

function getSourceBadge(food) {
  if (food.source === "custom") return { label: "Custom", bg: "#fef3c7", color: "#92400e" };
  if (food.source === "usda") return { label: food.dataType || "USDA", bg: "#dcfce7", color: "#166534" };
  if (food.source === "openfoodfacts") return { label: "Branded", bg: "#fce7f3", color: "#9d174d" };
  return { label: "Saved", bg: "#e0f2fe", color: "#1d4ed8" };
}

function getDefaultServingOption(food) {
  return (food?.servingOptions || []).find((option) => option.isDefault) || food?.servingOptions?.[0] || null;
}

function getFoodSummaryLine(food) {
  const defaultServing = getDefaultServingOption(food);
  if (defaultServing && defaultServing.grams > 0 && defaultServing.label !== "100 g") {
    const macros = calcMacrosForServing(food, defaultServing.grams);
    return `${defaultServing.label}: ${macros.calories} kcal · P ${macros.protein}g · C ${macros.carbs}g · F ${macros.fat}g`;
  }

  return `Per 100g: ${food.per100g.calories} kcal · P ${food.per100g.protein}g · C ${food.per100g.carbs}g · F ${food.per100g.fat}g`;
}

function FoodList({ foods, favoriteIds, onSelect, onToggleFavorite, onDelete, emptyText }) {
  if (!foods.length) {
    return <div style={{ color: "#4b6b8a", fontSize: "13px" }}>{emptyText}</div>;
  }

  return (
    <div style={{ display: "grid", gap: "8px", maxHeight: "340px", overflowY: "auto" }}>
      {foods.map((food) => {
        const badge = getSourceBadge(food);
        const isFavorite = favoriteIds.has(buildFoodKey(food));

        return (
          <div
            key={food.id || buildFoodKey(food)}
            style={{
              padding: "10px 12px",
              borderRadius: "12px",
              border: "2px solid #bfdbfe",
              background: "#f5faff",
            }}
          >
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <button
                type="button"
                onClick={() => onSelect(food)}
                style={{
                  flex: 1,
                  textAlign: "left",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>{food.name}</div>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      background: badge.bg,
                      color: badge.color,
                      padding: "3px 8px",
                      borderRadius: "999px",
                    }}
                  >
                    {badge.label}
                  </span>
                </div>
                {food.brand && <div style={{ color: "#6b8cb0", fontSize: "12px" }}>{food.brand}</div>}
                <div style={{ color: "#4b6b8a", fontSize: "12px", marginTop: "2px" }}>{getFoodSummaryLine(food)}</div>
              </button>
              <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => onToggleFavorite(food)}
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    border: "2px solid #bfdbfe",
                    background: isFavorite ? "#fef3c7" : "#ffffff",
                    color: isFavorite ? "#d97706" : "#6b8cb0",
                    fontSize: "16px",
                    cursor: "pointer",
                  }}
                  aria-label={isFavorite ? "Remove favorite" : "Add favorite"}
                  title={isFavorite ? "Remove favorite" : "Add favorite"}
                >
                  ★
                </button>
                {onDelete && (
                  <button
                    type="button"
                    onClick={() => onDelete(food)}
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      border: "2px solid #fecaca",
                      background: "#fff1f2",
                      color: "#dc2626",
                      fontSize: "16px",
                      cursor: "pointer",
                    }}
                    aria-label="Delete custom food"
                    title="Delete custom food"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function MacroTracker({ uid, date, triggerOpenSearch, healthSyncEnabled = false, healthTodayWorkouts = [] }) {
  const [targets, setTargets] = useState({ calories: 2000, protein: 150, carbs: 200, fat: 65 });
  const [foodLog, setFoodLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("log");
  const [showAddPanel, setShowAddPanel] = useState(false);

  const [addMode, setAddMode] = useState("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [lastSearchedQuery, setLastSearchedQuery] = useState("");
  const [baseResults, setBaseResults] = useState([]);
  const baseQueryRef = useRef("");
  const [selectedFood, setSelectedFood] = useState(null);
  const [servingGrams, setServingGrams] = useState("100");
  const [servingAmount, setServingAmount] = useState("100");
  const [servingUnit, setServingUnit] = useState("g");
  const [servingQuantity, setServingQuantity] = useState("1");
  const [mealLabel, setMealLabel] = useState("Breakfast");
  const [scanError, setScanError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [labelParsing, setLabelParsing] = useState(false);
  const [labelParseError, setLabelParseError] = useState("");
  const [showManualBarcode, setShowManualBarcode] = useState(false);
  const [manualBarcodeValue, setManualBarcodeValue] = useState("");
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [labelDraft, setLabelDraft] = useState(null);

  const [recentFoods, setRecentFoods] = useState([]);
  const [favoriteFoods, setFavoriteFoods] = useState([]);
  const [customFoods, setCustomFoods] = useState([]);

  const [manualName, setManualName] = useState("");
  const [manualBrand, setManualBrand] = useState("");
  const [manualServing, setManualServing] = useState("100");
  const [manualCal, setManualCal] = useState("");
  const [manualProtein, setManualProtein] = useState("");
  const [manualCarbs, setManualCarbs] = useState("");
  const [manualFat, setManualFat] = useState("");
  const [customFoodPendingDelete, setCustomFoodPendingDelete] = useState(null);

  const [editTargets, setEditTargets] = useState({ ...targets });
  const [effectiveTargets, setEffectiveTargets] = useState({ calories: 2000, protein: 150, carbs: 200, fat: 65, isOverride: false });
  const [overrideDraft, setOverrideDraft] = useState({});

  const [logError, setLogError] = useState("");

  const [trendsRange, setTrendsRange] = useState(7);
  const [trendsData, setTrendsData] = useState([]);
  const [trendsLoading, setTrendsLoading] = useState(false);

  const searchTimeout = useRef(null);
  const searchInputRef = useRef(null);
  const labelInputRef = useRef(null);
  const recognizerRef = useRef(null);
  const videoRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!uid || !date) return;
    setLoading(true);
    setOverrideDraft({});
    Promise.all([
      getMacroTargets(uid),
      getEffectiveMacroTargets(uid, date),
      getFoodLog(uid, date),
      getRecentFoods(uid),
      getFavoriteFoods(uid),
      getCustomFoods(uid),
    ])
      .then(([t, effective, log, recents, favorites, customs]) => {
        setTargets(t);
        setEditTargets(t);
        setEffectiveTargets(effective);
        setFoodLog(log);
        setRecentFoods(recents);
        setFavoriteFoods(favorites);
        setCustomFoods(customs);
      })
      .finally(() => setLoading(false));
  }, [uid, date]);

  // Open the search panel and autofocus when triggered from FAB shortcut
  useEffect(() => {
    if (!triggerOpenSearch) return;
    setView("log");
    setShowAddPanel(true);
    setAddMode("search");
    setSelectedFood(null);
    setSearchResults([]);
    setBaseResults([]);
    baseQueryRef.current = "";
    setSearchQuery("");
  }, [triggerOpenSearch]);

  // Autofocus the search input whenever the search panel opens in search mode
  useEffect(() => {
    if (showAddPanel && addMode === "search" && !selectedFood) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [showAddPanel, addMode, selectedFood]);

  const favoriteIds = useMemo(
    () => new Set(favoriteFoods.map((food) => buildFoodKey(food))),
    [favoriteFoods]
  );

  const totals = foodLog.reduce(
    (acc, entry) => ({
      calories: acc.calories + (entry.calories || 0),
      protein: acc.protein + (entry.protein || 0),
      carbs: acc.carbs + (entry.carbs || 0),
      fat: acc.fat + (entry.fat || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  const customMatches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return customFoods.filter((food) => {
      const haystack = `${food.name} ${food.brand}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [customFoods, searchQuery]);

  const [showCustomCreator, setShowCustomCreator] = useState(false);

  // Grams per 1 unit. For volume units (cup/tbsp/tsp) we check the food's serving
  // options first so we get the food-specific density, then fall back to water density.
  function getUnitGrams(unit, food) {
    if (unit === "g" || unit === "ml") return 1;
    if (unit === "oz") return 28.35;
    if (unit === "lb") return 453.592;
    if (unit === "fl oz") return 29.574;
    const volumeUnits = { cup: 240, tbsp: 15, tsp: 5 };
    if (!(unit in volumeUnits)) return 1;
    // Try to find an exact match in the food's serving options
    for (const opt of (food?.servingOptions || [])) {
      const m = opt.label.match(/^(\d+(?:\.\d+)?)\s*(cups?|tbsp|tsp)$/i);
      if (m && m[2].replace(/s$/i, "").toLowerCase() === unit.toLowerCase()) {
        return opt.grams / parseFloat(m[1]);
      }
    }
    return volumeUnits[unit];
  }

  function calcGramsFromServing(amount, unit, quantity, food) {
    const a = parseFloat(amount) || 0;
    const q = parseFloat(quantity) || 1;
    return Math.round(a * getUnitGrams(unit, food) * q * 10) / 10;
  }

  function initServingFromOption(option) {
    if (!option) return { amount: "100", unit: "g" };
    const patterns = [
      [/^(\d+(?:\.\d+)?)\s*oz$/i, "oz"],
      [/^(\d+(?:\.\d+)?)\s*cups?$/i, "cup"],
      [/^(\d+(?:\.\d+)?)\s*tbsp$/i, "tbsp"],
      [/^(\d+(?:\.\d+)?)\s*tsp$/i, "tsp"],
      [/^(\d+(?:\.\d+)?)\s*fl\s*oz$/i, "fl oz"],
      [/^(\d+(?:\.\d+)?)\s*lb$/i, "lb"],
      [/^(\d+(?:\.\d+)?)\s*ml$/i, "ml"],
    ];
    for (const [re, unit] of patterns) {
      const m = option.label.match(re);
      if (m) return { amount: m[1], unit };
    }
    return { amount: String(option.grams), unit: "g" };
  }

  function closeBarcodeScanner() {
    if (scanIntervalRef.current) { clearInterval(scanIntervalRef.current); scanIntervalRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setShowBarcodeScanner(false);
  }

  function resetPickerState(nextView = null) {
    closeBarcodeScanner();
    setSelectedFood(null);
    setSearchResults([]);
    setBaseResults([]);
    baseQueryRef.current = "";
    setSearchQuery("");
    setScanError("");
    setLabelParseError("");
    setLabelDraft(null);
    setShowManualBarcode(false);
    setManualBarcodeValue("");
    setServingAmount("100");
    setServingUnit("g");
    setServingQuantity("1");
    setServingGrams("100");
    setShowAddPanel(false);
    if (nextView) setView(nextView);
  }

  function clientRank(food, query) {
    const name = (food.name || "").toLowerCase();
    const brand = (food.brand || "").toLowerCase();
    const q = query.toLowerCase().trim();
    if (!q || !name) return 0;
    const nameWords = name.split(/[\s,]+/).filter(Boolean);
    const brandWords = brand.split(/[\s,]+/).filter(Boolean);
    const allWords = [...nameWords, ...brandWords];
    const qWords = q.split(/\s+/).filter(Boolean);
    const flatName = nameWords.join(" ");
    const flatQ = qWords.join(" ");

    let score = 0;

    // Mirror server data-type quality weights
    const dt = food.dataType || "";
    if (dt === "Foundation") score += 1400;
    else if (dt === "SR Legacy") score += 550;
    else if (dt === "Survey (FNDDS)") score += 400;
    else if (dt === "Branded") score += 200;

    // Hard penalize: if a distinctive query word (≥4 chars) doesn't appear anywhere in
    // the food name or brand, this item clearly doesn't belong in results for this query.
    // e.g. "fairlife" not found in Foundation cheese → -1500 overrides +1400 Foundation bonus.
    for (const qw of qWords) {
      if (qw.length >= 4 && !allWords.some((w) => w.startsWith(qw))) {
        score -= 1500;
      }
    }

    // Substring match on name — primary signal
    if (flatName.startsWith(flatQ)) score += 1200;
    else if (flatName.includes(flatQ)) score += 700;

    // All query words appear as word-level prefixes (in name or brand)
    if (qWords.length > 0 && qWords.every((qw) => allWords.some((w) => w.startsWith(qw)))) score += 350;

    // Prefer simpler names
    score -= nameWords.length * 15;

    // Penalize processing/preparation terms not in query
    const BAD_TERMS = ["breaded", "battered", "fried", "sauteed", "stewed", "tenders", "nuggets",
      "strips", "fast foods", "fat-free", "low-fat", "seasoned", "flavored", "deli", "lunchmeat"];
    for (const term of BAD_TERMS) {
      if (flatName.includes(term) && !flatQ.includes(term)) score -= 150;
    }

    return score;
  }

  function handleSearchChange(value) {
    setSearchQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    const trimmed = value.trim();
    if (!trimmed) {
      setSearchResults([]);
      setBaseResults([]);
      baseQueryRef.current = "";
      return;
    }

    // Only reuse cached results if the new query is a refinement of the old one
    // (either extends it or trims back into it). If it's a different search entirely, clear.
    const cachedQuery = baseQueryRef.current;
    const isRefinement = cachedQuery &&
      (trimmed.startsWith(cachedQuery) || cachedQuery.startsWith(trimmed));

    if (baseResults.length > 0 && !isRefinement) {
      setBaseResults([]);
      setSearchResults([]);
      baseQueryRef.current = "";
    } else if (baseResults.length > 0 && isRefinement) {
      const reranked = [...baseResults]
        .sort((a, b) => clientRank(b, trimmed) - clientRank(a, trimmed))
        .slice(0, 25);
      setSearchResults(reranked);
    }

    // Determine the stable query for deciding when a new USDA fetch is needed.
    // Include the last word once it's ≥4 chars — at that point it's a meaningful word
    // that changes what USDA should search for (e.g. "chicken pizz" → fetch "chicken pizza").
    // For short last words (1-3 chars) keep the previous stable prefix so we don't
    // spam fetches on every keystroke while still typing a new word.
    const words = trimmed.split(/\s+/);
    const lastWord = words[words.length - 1];
    const stableQuery = (value.endsWith(" ") || words.length === 1 || lastWord.length >= 4)
      ? trimmed
      : words.slice(0, -1).join(" ");

    if (stableQuery === baseQueryRef.current && baseResults.length > 0) return;

    // Show loading immediately so the user sees feedback during the debounce window
    setSearchLoading(true);

    // New stable query — fetch fresh results from USDA (debounced so we don't spam on every letter)
    searchTimeout.current = setTimeout(async () => {
      try {
        const results = await searchFoodByName(trimmed);
        setBaseResults(results);
        baseQueryRef.current = stableQuery;
        setLastSearchedQuery(trimmed);
        const reranked = [...results]
          .sort((a, b) => clientRank(b, trimmed) - clientRank(a, trimmed))
          .slice(0, 25);
        setSearchResults(reranked);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
  }

  function handleSearchKeyDown(e) {
    if (e.key !== "Enter") return;
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    setSearchLoading(true);
    searchFoodByName(trimmed).then((results) => {
      setBaseResults(results);
      baseQueryRef.current = trimmed;
      setLastSearchedQuery(trimmed);
      const reranked = [...results]
        .sort((a, b) => clientRank(b, trimmed) - clientRank(a, trimmed))
        .slice(0, 25);
      setSearchResults(reranked);
    }).finally(() => setSearchLoading(false));
  }

  async function selectFood(food) {
    const detailedFood = await getFoodDetails(food);
    const defaultOption = (detailedFood.servingOptions || []).find((option) => option.isDefault) || detailedFood.servingOptions?.[0];
    const { amount, unit } = initServingFromOption(defaultOption);
    setSelectedFood(detailedFood);
    setServingAmount(amount);
    setServingUnit(unit);
    setServingQuantity("1");
    setServingGrams(String(calcGramsFromServing(amount, unit, "1", detailedFood)));
    setSearchResults([]);
    setSearchQuery("");
    setScanError("");
  }

  function getPreviewMacros() {
    if (!selectedFood) return null;
    return calcMacrosForServing(selectedFood, parseFloat(servingGrams) || 100);
  }

  async function refreshSavedFoods() {
    const [recents, favorites, customs] = await Promise.all([
      getRecentFoods(uid),
      getFavoriteFoods(uid),
      getCustomFoods(uid),
    ]);
    setRecentFoods(recents);
    setFavoriteFoods(favorites);
    setCustomFoods(customs);
  }

  async function handleAddFood() {
    if (!selectedFood) return;
    const macros = getPreviewMacros();
    const entry = {
      name: selectedFood.name,
      brand: selectedFood.brand || "",
      meal: mealLabel,
      servingGrams: parseFloat(servingGrams) || 100,
      servingQuantity: parseFloat(servingQuantity) || 1,
      servingLabel: `${servingAmount} ${servingUnit}`,
      source: selectedFood.source || "usda",
      sourceId: selectedFood.sourceId || selectedFood.id || "",
      ...macros,
    };

    const tempId = `temp-${Date.now()}`;
    setFoodLog((prev) => [...prev, { ...entry, id: tempId }]);
    setSelectedFood(null);
    setServingAmount("100");
    setServingUnit("g");
    setServingQuantity("1");
    setShowAddPanel(false);
    setLogError("");

    try {
      await Promise.all([
        addFoodEntry(uid, date, entry),
        saveRecentFood(uid, selectedFood, parseFloat(servingGrams) || 100),
      ]);
      const [freshLog, recents] = await Promise.all([getFoodLog(uid, date), getRecentFoods(uid)]);
      setFoodLog(freshLog);
      setRecentFoods(recents);
    } catch {
      setFoodLog((prev) => prev.filter((e) => e.id !== tempId));
      setLogError("Couldn't log food. Please try again.");
    }
  }

  async function handleCreateCustomFood() {
    if (!manualName.trim()) return;

    const serving = parseFloat(manualServing) || 100;
    const customFood = await createCustomFood(uid, {
      source: "custom",
      name: manualName.trim(),
      brand: manualBrand.trim(),
      servingSize: serving,
      servingUnit: "g",
      per100g: {
        calories: Math.round(((parseFloat(manualCal) || 0) * 100) / serving),
        protein: ((parseFloat(manualProtein) || 0) * 100) / serving,
        carbs: ((parseFloat(manualCarbs) || 0) * 100) / serving,
        fat: ((parseFloat(manualFat) || 0) * 100) / serving,
      },
    });

    await saveRecentFood(uid, customFood);
    await refreshSavedFoods();

    setManualName("");
    setManualBrand("");
    setManualServing("100");
    setManualCal("");
    setManualProtein("");
    setManualCarbs("");
    setManualFat("");
    selectFood(customFood);
  }

  async function handleDelete(entryId) {
    setFoodLog((prev) => prev.filter((entry) => entry.id !== entryId));
    await deleteFoodEntry(uid, date, entryId);
  }

  async function handleToggleFavorite(food) {
    const key = buildFoodKey(food);
    const isFavorite = favoriteIds.has(key);

    if (isFavorite) {
      await removeFavoriteFood(uid, food);
      setFavoriteFoods((prev) => prev.filter((item) => buildFoodKey(item) !== key));
      return;
    }

    await saveFavoriteFood(uid, food);
    const favorites = await getFavoriteFoods(uid);
    setFavoriteFoods(favorites);
  }

  async function confirmDeleteCustomFood() {
    if (!customFoodPendingDelete) return;
    const foodToDelete = customFoodPendingDelete;
    setCustomFoodPendingDelete(null);
    await deleteCustomFood(uid, foodToDelete);
    setCustomFoods((prev) => prev.filter((food) => food.id !== foodToDelete.id));
    setRecentFoods((prev) =>
      prev.filter((food) => !(food.source === "custom" && food.sourceId === foodToDelete.sourceId))
    );
    setFavoriteFoods((prev) =>
      prev.filter((food) => !(food.source === "custom" && food.sourceId === foodToDelete.sourceId))
    );
  }

  async function handleSaveTargets() {
    const nextTargets = {
      calories: parseInt(editTargets.calories) || 2000,
      protein: parseInt(editTargets.protein) || 150,
      carbs: parseInt(editTargets.carbs) || 200,
      fat: parseInt(editTargets.fat) || 65,
    };
    setTargets(nextTargets);
    await saveMacroTargets(uid, nextTargets);
    const updated = await getEffectiveMacroTargets(uid, date);
    setEffectiveTargets(updated);
    setView("log");
  }

  async function handleSaveOverride() {
    const getValue = (key) => {
      const v = parseInt(overrideDraft[key] !== undefined ? overrideDraft[key] : effectiveTargets[key]);
      return Number.isFinite(v) && v > 0 ? v : effectiveTargets[key];
    };
    const override = {
      calories: getValue("calories"),
      protein: getValue("protein"),
      carbs: getValue("carbs"),
      fat: getValue("fat"),
    };
    await saveDailyTargetOverride(uid, date, override);
    const updated = await getEffectiveMacroTargets(uid, date);
    setEffectiveTargets(updated);
    setOverrideDraft({ calories: override.calories, protein: override.protein, carbs: override.carbs, fat: override.fat });
  }

  async function handleClearOverride() {
    await saveDailyTargetOverride(uid, date, null);
    const updated = await getEffectiveMacroTargets(uid, date);
    setEffectiveTargets(updated);
    setOverrideDraft({});
  }

  function handleVoiceSearch() {
    if (isListening) {
      recognizerRef.current?.stop();
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Voice input is not supported on this browser.");
      return;
    }
    const recog = new SpeechRecognition();
    recognizerRef.current = recog;
    recog.lang = "en-US";
    recog.continuous = false;
    recog.interimResults = false;
    recog.onresult = (e) => {
      const transcript = Array.from(e.results).map((r) => r[0].transcript).join(" ").trim();
      setIsListening(false);
      if (transcript) handleSearchChange(transcript);
    };
    recog.onerror = () => setIsListening(false);
    recog.onend = () => setIsListening(false);
    recog.start();
    setIsListening(true);
  }

  async function openBarcodeScanner() {
    setScanError("");
    setShowManualBarcode(false);
    if (!("BarcodeDetector" in window)) {
      setShowManualBarcode(true);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      setShowBarcodeScanner(true);
      // Attach stream to video element after render
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
        const detector = new window.BarcodeDetector({
          formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf"],
        });
        scanIntervalRef.current = setInterval(async () => {
          if (!videoRef.current || videoRef.current.readyState < 2) return;
          try {
            const barcodes = await detector.detect(videoRef.current);
            if (barcodes.length > 0) {
              const code = barcodes[0].rawValue;
              closeBarcodeScanner();
              await lookupBarcode(code);
            }
          } catch { /* frame not ready */ }
        }, 300);
      }, 100);
    } catch {
      setScanError("Camera access denied. Type the barcode number below instead.");
      setShowManualBarcode(true);
    }
  }

  async function lookupBarcode(code) {
    setScanning(true);
    setScanError("");
    try {
      const food = await searchFoodByBarcode(code);
      if (!food) {
        setScanError(`No food found for barcode ${code}.`);
        setShowManualBarcode(true);
        return;
      }
      await selectFood(food);
    } catch {
      setScanError("Lookup failed. Please try again.");
    } finally {
      setScanning(false);
    }
  }

  async function handleManualBarcodeSubmit() {
    const code = manualBarcodeValue.trim();
    if (!code) return;
    setScanning(true);
    setScanError("");
    try {
      const food = await searchFoodByBarcode(code);
      if (!food) {
        setScanError(`No food found for barcode ${code}.`);
        return;
      }
      setShowManualBarcode(false);
      setManualBarcodeValue("");
      await selectFood(food);
    } catch {
      setScanError("Lookup failed. Please try again.");
    } finally {
      setScanning(false);
    }
  }

  async function fileToJpegBase64(file, maxDim = 1280, quality = 0.9) {
    // Always draw through canvas so the output is always JPEG regardless of input format
    // (camera photos can be HEIC, WebP, PNG — cloud function expects JPEG)
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (w === 0 || h === 0) { URL.revokeObjectURL(url); reject(new Error("Image dimensions are 0")); return; }
        const scale = Math.min(1, maxDim / Math.max(w, h));
        w = Math.round(w * scale);
        h = Math.round(h * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { URL.revokeObjectURL(url); reject(new Error("Canvas 2D context unavailable")); return; }
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        const base64 = dataUrl.split(",")[1];
        if (!base64) { reject(new Error("Canvas toDataURL produced no data")); return; }
        resolve(base64);
      };
      img.onerror = (e) => { URL.revokeObjectURL(url); reject(new Error("Image failed to load: " + e)); };
      img.src = url;
    });
  }

  async function handleNutritionLabelFile(file) {
    setLabelParseError("");
    setLabelDraft(null);
    setLabelParsing(true);
    try {
      const base64 = await fileToJpegBase64(file);
      const food = await parseNutritionLabel(base64);
      if (!food) {
        setLabelParseError("Label read returned no data. Try a closer shot of just the nutrition facts panel.");
        return;
      }
      setLabelDraft({
        name: food.name != null ? String(food.name) : "",
        servingSize: food.servingSize != null ? String(Math.round(food.servingSize)) : "",
        calories: food.calories != null ? String(Math.round(food.calories)) : "",
        protein: food.protein != null ? String(food.protein) : "",
        carbs: food.carbs != null ? String(food.carbs) : "",
        fat: food.fat != null ? String(food.fat) : "",
      });
    } catch (err) {
      console.error("Nutrition label scan failed:", err?.code, err?.message, err);
      const msg = err?.message || "";
      if (msg.includes("context") || msg.includes("canvas") || msg.includes("dimensions")) {
        setLabelParseError("Couldn't process the photo on this device. Try a different photo.");
      } else if (err?.code === "internal") {
        setLabelParseError("AI read failed — try again or take a closer photo of just the nutrition facts panel.");
      } else {
        setLabelParseError(`Scan failed: ${msg || "unknown error"}. Please try again.`);
      }
    } finally {
      setLabelParsing(false);
    }
  }

  function confirmLabelDraft() {
    if (!labelDraft) return;
    setAddMode("custom");
    setShowCustomCreator(true);
    if (labelDraft.name) setManualName(labelDraft.name);
    if (labelDraft.servingSize) setManualServing(labelDraft.servingSize);
    if (labelDraft.calories) setManualCal(labelDraft.calories);
    if (labelDraft.protein) setManualProtein(labelDraft.protein);
    if (labelDraft.carbs) setManualCarbs(labelDraft.carbs);
    if (labelDraft.fat) setManualFat(labelDraft.fat);
    setLabelDraft(null);
  }

  async function loadTrends(range, baseDate) {
    setTrendsLoading(true);
    const dates = Array.from({ length: range }, (_, i) => {
      const d = new Date(baseDate);
      d.setDate(d.getDate() - (range - 1 - i));
      return d.toISOString().slice(0, 10);
    });
    try {
      const data = await getMacroLogRange(uid, dates);
      setTrendsData(data);
    } finally {
      setTrendsLoading(false);
    }
  }

  if (loading) {
    return (
      <div style={{ color: "#4b6b8a", padding: "20px", textAlign: "center" }}>
        Loading macro tracker...
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
        {[
          { key: "log", label: "Today’s Log" },
          { key: "trends", label: "Trends" },
          { key: "targets", label: "Targets" },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => {
              resetPickerState(tab.key);
              if (tab.key === "trends") loadTrends(trendsRange, date);
            }}
            style={{
              padding: "9px 14px",
              borderRadius: "12px",
              border: view === tab.key ? "2px solid #2563eb" : "2px solid #bfdbfe",
              background: view === tab.key ? "#e0f2fe" : "#f5faff",
              color: "#1e3a5f",
              fontWeight: "bold",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Health activity summary — shown when workout sync is on */}
      {healthSyncEnabled && healthTodayWorkouts.length > 0 && (
        <div style={{ background: "#f0fdf4", borderRadius: "14px", padding: "12px 14px", border: "1.5px solid #86efac", marginBottom: "12px" }}>
          <div style={{ fontWeight: "bold", color: "#15803d", fontSize: "13px", marginBottom: "8px" }}>
            Today's Activity · Health Sync
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {healthTodayWorkouts.map((w, i) => {
              const mins = Math.round((w.durationSeconds || 0) / 60);
              return (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#166534" }}>
                  <span>{w.type}</span>
                  <span style={{ color: "#4b6b8a" }}>
                    {mins > 0 ? `${mins} min` : ""}
                    {w.calories > 0 ? `${mins > 0 ? " · " : ""}${w.calories} kcal` : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {view === "log" && (
        <>
          <div style={{ ...cardStyle, display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
            {effectiveTargets.isOverride && (
              <div style={{ width: "100%", padding: "6px 10px", borderRadius: "8px", background: "#fef3c7", border: "1px solid #fde68a", color: "#92400e", fontSize: "12px", fontWeight: "bold", textAlign: "center" }}>
                Custom targets active for {date}
              </div>
            )}
            <CalorieRing value={totals.calories} target={effectiveTargets.calories} />
            <div style={{ width: "100%" }}>
              <MacroBar label="Protein" value={totals.protein} target={effectiveTargets.protein} color="#34d399" />
              <MacroBar label="Carbs" value={totals.carbs} target={effectiveTargets.carbs} color="#fbbf24" />
              <MacroBar label="Fat" value={totals.fat} target={effectiveTargets.fat} color="#f87171" />
            </div>
          </div>

          {logError && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 14px", color: "#dc2626", fontSize: "13px", marginTop: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {logError}
              <button onClick={() => setLogError("")} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "16px", lineHeight: 1, flexShrink: 0 }}>×</button>
            </div>
          )}

          {/* + Add Food button */}
          {!showAddPanel && (
            <button
              type="button"
              onClick={() => { setShowAddPanel(true); setAddMode("search"); setSelectedFood(null); setSearchQuery(""); setSearchResults([]); }}
              style={{
                width: "100%", marginTop: "8px", padding: "14px 0",
                borderRadius: "16px", border: "2px dashed #93c5fd",
                background: "transparent", color: "#2563eb",
                fontSize: "20px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
              }}
            >
              <span>+</span>
              <span style={{ fontSize: "14px", fontWeight: "bold" }}>Add Food</span>
            </button>
          )}

          {/* Inline add food panel */}
          {showAddPanel && (
            <div id="macro-add-panel" style={{ ...cardStyle, marginTop: "8px" }}>
              {/* Meal picker */}
              <div style={{ marginBottom: "14px" }}>
                <div style={{ color: "#4b6b8a", fontSize: "12px", fontWeight: "bold", marginBottom: "6px", textTransform: "uppercase" }}>
                  Meal
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {MEAL_LABELS.map((meal) => (
                    <button
                      key={meal}
                      type="button"
                      onClick={() => setMealLabel(meal)}
                      style={{
                        padding: "7px 12px",
                        borderRadius: "10px",
                        border: mealLabel === meal ? "2px solid #2563eb" : "2px solid #bfdbfe",
                        background: mealLabel === meal ? "#e0f2fe" : "#f5faff",
                        color: "#1e3a5f",
                        fontWeight: mealLabel === meal ? "bold" : "normal",
                        fontSize: "13px",
                        cursor: "pointer",
                      }}
                    >
                      {meal}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mode tabs */}
              <div style={{ display: "flex", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
                {[
                  { key: "search", label: "Search" },
                  { key: "recent", label: "Recent" },
                  { key: "favorites", label: "Favorites" },
                  { key: "custom", label: "Custom" },
                ].map((mode) => (
                  <button
                    key={mode.key}
                    type="button"
                    onClick={() => {
                      setAddMode(mode.key);
                      setSelectedFood(null);
                      setSearchResults([]);
                      setSearchQuery("");
                      setScanError("");
                      setLabelParseError("");
                      setShowCustomCreator(false);
                      setShowManualBarcode(false);
                    }}
                    style={{
                      padding: "9px 10px",
                      borderRadius: "10px",
                      border: addMode === mode.key ? "2px solid #2563eb" : "2px solid #bfdbfe",
                      background: addMode === mode.key ? "#e0f2fe" : "#f5faff",
                      color: "#1e3a5f",
                      fontWeight: "bold",
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>

              {addMode === "search" && !selectedFood && (
                <>
                  {/* Search bar with voice, label-camera, and barcode icons */}
                  <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "10px" }}>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search foods..."
                      value={searchQuery}
                      onChange={(event) => handleSearchChange(event.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    {/* Mic icon */}
                    <button type="button" title={isListening ? "Stop listening" : "Voice search"} onClick={handleVoiceSearch}
                      style={{ width: "42px", height: "42px", borderRadius: "12px", flexShrink: 0, border: isListening ? "2px solid #2563eb" : "2px solid #bfdbfe", background: isListening ? "#e0f2fe" : "#f5faff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: isListening ? "#2563eb" : "#4b6b8a" }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="2" width="6" height="11" rx="3"/>
                        <path d="M5 10a7 7 0 0 0 14 0"/>
                        <line x1="12" y1="17" x2="12" y2="22"/>
                        <line x1="8" y1="22" x2="16" y2="22"/>
                      </svg>
                    </button>
                    {/* Camera icon (nutrition label) */}
                    <button type="button" title="Scan nutrition label" onClick={() => labelInputRef.current?.click()} disabled={labelParsing}
                      style={{ width: "42px", height: "42px", borderRadius: "12px", flexShrink: 0, border: "2px solid #bfdbfe", background: "#f5faff", cursor: labelParsing ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#4b6b8a", opacity: labelParsing ? 0.5 : 1 }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                        <circle cx="12" cy="13" r="4"/>
                      </svg>
                    </button>
                    {/* Barcode icon */}
                    <button type="button" title="Scan barcode" onClick={openBarcodeScanner} disabled={scanning}
                      style={{ width: "42px", height: "42px", borderRadius: "12px", flexShrink: 0, border: "2px solid #bfdbfe", background: "#f5faff", cursor: scanning ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#4b6b8a", opacity: scanning ? 0.5 : 1 }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="1" y="3" width="2" height="18"/><rect x="4.5" y="3" width="1" height="18"/>
                        <rect x="7" y="3" width="2.5" height="18"/><rect x="11" y="3" width="1" height="18"/>
                        <rect x="13.5" y="3" width="2" height="18"/><rect x="17" y="3" width="1" height="18"/>
                        <rect x="19.5" y="3" width="1" height="18"/><rect x="22" y="3" width="1" height="18"/>
                      </svg>
                    </button>
                    {/* Hidden file input for nutrition label */}
                    <input ref={labelInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                      onChange={(e) => { if (e.target.files?.[0]) { handleNutritionLabelFile(e.target.files[0]); e.target.value = ""; } }}
                    />
                  </div>

                  {/* Status/error messages for icon actions */}
                  {isListening && (
                    <div style={{ color: "#2563eb", fontSize: "13px", marginBottom: "8px", fontWeight: "bold" }}>Listening... speak now</div>
                  )}
                  {labelParsing && (
                    <div style={{ color: "#4b6b8a", fontSize: "13px", marginBottom: "8px" }}>Reading nutrition label...</div>
                  )}
                  {labelParseError && (
                    <div style={{ color: "#dc2626", fontSize: "13px", marginBottom: "8px", padding: "8px 12px", background: "#fef2f2", borderRadius: "10px", border: "1px solid #fecaca" }}>
                      {labelParseError}
                    </div>
                  )}
                  {/* Label confirmation card */}
                  {labelDraft && (
                    <div style={{ marginBottom: "12px", padding: "14px", borderRadius: "14px", background: "#f0f9ff", border: "2px solid #bfdbfe" }}>
                      <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", marginBottom: "2px" }}>Check & confirm</div>
                      <div style={{ color: "#4b6b8a", fontSize: "12px", marginBottom: "12px" }}>Fill in any blank fields, then tap Confirm.</div>
                      {/* Name — full width text field */}
                      <div style={{ marginBottom: "10px" }}>
                        <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "3px" }}>Product name</div>
                        <input
                          type="text"
                          placeholder="e.g. Greek Yogurt"
                          value={labelDraft.name}
                          onChange={(e) => setLabelDraft((prev) => ({ ...prev, name: e.target.value }))}
                          style={{ ...inputStyle, fontSize: "14px" }}
                        />
                      </div>
                      {/* Numeric fields — 2-column grid */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "12px" }}>
                        {[
                          { key: "servingSize", label: "Serving size (g)" },
                          { key: "calories", label: "Calories (kcal)" },
                          { key: "protein", label: "Protein (g)" },
                          { key: "carbs", label: "Carbs (g)" },
                          { key: "fat", label: "Fat (g)" },
                        ].map(({ key, label }) => (
                          <div key={key}>
                            <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "3px" }}>{label}</div>
                            <input
                              type="number" min="0" step="0.1"
                              placeholder="—"
                              value={labelDraft[key]}
                              onChange={(e) => setLabelDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                              onFocus={(e) => e.target.select()}
                              style={{ ...inputStyle, fontSize: "14px", borderColor: labelDraft[key] ? "#bfdbfe" : "#fca5a5" }}
                            />
                          </div>
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button type="button" onClick={confirmLabelDraft} style={{ ...btnPrimary, flex: 1 }}>Confirm</button>
                        <button type="button" onClick={() => setLabelDraft(null)} style={btnSecondary}>Cancel</button>
                      </div>
                    </div>
                  )}
                  {scanError && (
                    <div style={{ color: "#dc2626", fontSize: "13px", marginBottom: "8px", padding: "8px 12px", background: "#fef2f2", borderRadius: "10px", border: "1px solid #fecaca" }}>
                      {scanError}
                    </div>
                  )}
                  {scanning && !showManualBarcode && (
                    <div style={{ color: "#4b6b8a", fontSize: "13px", marginBottom: "8px" }}>Looking up barcode...</div>
                  )}
                  {showManualBarcode && (
                    <div style={{ marginBottom: "10px" }}>
                      <div style={{ color: "#4b6b8a", fontSize: "12px", marginBottom: "6px" }}>Type or paste barcode number:</div>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <input type="text" placeholder="e.g. 012345678901" value={manualBarcodeValue}
                          onChange={(e) => setManualBarcodeValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleManualBarcodeSubmit(); }}
                          style={{ ...inputStyle, flex: 1 }}
                        />
                        <button type="button" onClick={handleManualBarcodeSubmit} disabled={scanning || !manualBarcodeValue.trim()}
                          style={{ ...btnPrimary, opacity: (scanning || !manualBarcodeValue.trim()) ? 0.5 : 1 }}
                        >
                          {scanning ? "..." : "Go"}
                        </button>
                      </div>
                    </div>
                  )}

                  {customMatches.length > 0 && (
                    <div style={{ marginBottom: "12px" }}>
                      <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "13px", marginBottom: "8px" }}>Your foods</div>
                      <FoodList
                        foods={customMatches}
                        favoriteIds={favoriteIds}
                        onSelect={selectFood}
                        onToggleFavorite={handleToggleFavorite}
                        emptyText=""
                      />
                    </div>
                  )}
                  {searchLoading && <div style={{ color: "#4b6b8a", fontSize: "13px", marginBottom: "8px" }}>Searching USDA...</div>}
                  {searchResults.length > 0 && (
                    <div>
                      <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "13px", marginBottom: "8px" }}>USDA foods</div>
                      <FoodList
                        foods={searchResults}
                        favoriteIds={favoriteIds}
                        onSelect={selectFood}
                        onToggleFavorite={handleToggleFavorite}
                        emptyText="No USDA results yet."
                      />
                    </div>
                  )}
                  {lastSearchedQuery && !searchLoading && searchResults.length === 0 && customMatches.length === 0 && lastSearchedQuery === searchQuery && (
                    <div style={{ color: "#4b6b8a", fontSize: "13px" }}>No results found. Try another search or add a custom food.</div>
                  )}
                </>
              )}

              {addMode === "recent" && !selectedFood && (
                <FoodList
                  foods={recentFoods}
                  favoriteIds={favoriteIds}
                  onSelect={selectFood}
                  onToggleFavorite={handleToggleFavorite}
                  emptyText="Your recently logged foods will show up here."
                />
              )}

              {addMode === "favorites" && !selectedFood && (
                <FoodList
                  foods={favoriteFoods}
                  favoriteIds={favoriteIds}
                  onSelect={selectFood}
                  onToggleFavorite={handleToggleFavorite}
                  emptyText="Favorite foods will show up here."
                />
              )}

              {addMode === "custom" && !selectedFood && (
                <div style={{ display: "grid", gap: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
                    <div style={{ color: "#4b6b8a", fontSize: "13px" }}>Browse foods you created.</div>
                    <button
                      type="button"
                      onClick={() => setShowCustomCreator((prev) => !prev)}
                      style={{
                        width: "38px", height: "38px", borderRadius: "12px",
                        border: "2px solid #2563eb",
                        background: showCustomCreator ? "#e0f2fe" : "#f5faff",
                        color: "#2563eb", fontSize: "22px", lineHeight: 1,
                        cursor: "pointer", flexShrink: 0,
                      }}
                    >
                      +
                    </button>
                  </div>
                  {showCustomCreator && (
                    <div style={{ display: "grid", gap: "10px", padding: "14px", borderRadius: "14px", background: "#f0f9ff", border: "1px solid #dbeafe" }}>
                      <input type="text" placeholder="Food name *" value={manualName} onChange={(e) => setManualName(e.target.value)} style={inputStyle} />
                      <input type="text" placeholder="Brand (optional)" value={manualBrand} onChange={(e) => setManualBrand(e.target.value)} style={inputStyle} />
                      <div>
                        <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", marginBottom: "4px" }}>DEFAULT SERVING (g)</div>
                        <input type="number" min="1" value={manualServing} onChange={(e) => setManualServing(e.target.value)} style={inputStyle} />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                        {[["CALORIES", manualCal, setManualCal], ["PROTEIN (g)", manualProtein, setManualProtein], ["CARBS (g)", manualCarbs, setManualCarbs], ["FAT (g)", manualFat, setManualFat]].map(([label, val, setter]) => (
                          <div key={label}>
                            <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", marginBottom: "4px" }}>{label}</div>
                            <input type="number" min="0" step="0.1" value={val} onChange={(e) => setter(e.target.value)} onFocus={(e) => e.target.select()} style={inputStyle} />
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={handleCreateCustomFood} disabled={!manualName.trim()} style={{ ...btnPrimary, opacity: !manualName.trim() ? 0.5 : 1 }}>
                        Save Custom Food
                      </button>
                    </div>
                  )}
                  <FoodList
                    foods={customFoods}
                    favoriteIds={favoriteIds}
                    onSelect={selectFood}
                    onToggleFavorite={handleToggleFavorite}
                    onDelete={setCustomFoodPendingDelete}
                    emptyText="Custom foods you create will show up here."
                  />
                </div>
              )}

              {selectedFood && (
                <div>
                  <div style={{ ...cardStyle, marginBottom: "12px", background: "#f0f9ff", border: "2px solid #bfdbfe" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px" }}>{selectedFood.name}</div>
                        {selectedFood.brand && <div style={{ color: "#6b8cb0", fontSize: "13px", marginTop: "2px" }}>{selectedFood.brand}</div>}
                        <div style={{ color: "#4b6b8a", fontSize: "12px", marginTop: "4px" }}>{getFoodSummaryLine(selectedFood)}</div>
                      </div>
                      <button type="button" onClick={() => handleToggleFavorite(selectedFood)} style={{ ...btnSecondary, padding: "9px 12px", background: favoriteIds.has(buildFoodKey(selectedFood)) ? "#fef3c7" : "#f5faff" }}>
                        {favoriteIds.has(buildFoodKey(selectedFood)) ? "★ Favorite" : "☆ Favorite"}
                      </button>
                    </div>
                  </div>

                  <div style={{ marginBottom: "12px" }}>
                    <div style={{ color: "#4b6b8a", fontSize: "12px", fontWeight: "bold", marginBottom: "6px", textTransform: "uppercase" }}>Serving</div>
                    <div style={{ display: "grid", gap: "8px", gridTemplateColumns: "1fr auto 1fr" }}>
                      <div>
                        <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "3px" }}>Amount</div>
                        <input
                          type="number" min="0" step="0.5" value={servingAmount}
                          onChange={(e) => {
                            setServingAmount(e.target.value);
                            setServingGrams(String(calcGramsFromServing(e.target.value, servingUnit, servingQuantity, selectedFood)));
                          }}
                          onFocus={(e) => e.target.select()}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "3px" }}>Unit</div>
                        <select
                          value={servingUnit}
                          onChange={(e) => {
                            const newUnit = e.target.value;
                            const oldGrams = getUnitGrams(servingUnit, selectedFood) * (parseFloat(servingAmount) || 0);
                            const newUnitGrams = getUnitGrams(newUnit, selectedFood);
                            const newAmount = newUnitGrams > 0
                              ? String(Math.round(oldGrams / newUnitGrams * 100) / 100)
                              : servingAmount;
                            setServingUnit(newUnit);
                            setServingAmount(newAmount);
                            setServingGrams(String(calcGramsFromServing(newAmount, newUnit, servingQuantity, selectedFood)));
                          }}
                          style={{ ...inputStyle, paddingLeft: "10px" }}
                        >
                          <option value="oz">oz</option>
                          <option value="cup">cup</option>
                          <option value="tbsp">tbsp</option>
                          <option value="tsp">tsp</option>
                          <option value="fl oz">fl oz</option>
                          <option value="lb">lb</option>
                          <option value="ml">ml</option>
                          <option value="g">g</option>
                        </select>
                      </div>
                      <div>
                        <div style={{ color: "#4b6b8a", fontSize: "11px", marginBottom: "3px" }}>Servings</div>
                        <input
                          type="number" min="0.25" step="0.25" value={servingQuantity}
                          onChange={(e) => {
                            setServingQuantity(e.target.value);
                            setServingGrams(String(calcGramsFromServing(servingAmount, servingUnit, e.target.value, selectedFood)));
                          }}
                          onFocus={(e) => e.target.select()}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                    <div style={{ color: "#4b6b8a", fontSize: "12px", marginTop: "6px" }}>
                      Total amount: {Math.round((parseFloat(servingGrams) || 0) * 10) / 10} g
                    </div>
                  </div>

                  {(() => {
                    const preview = getPreviewMacros();
                    if (!preview) return null;
                    return (
                      <div style={{ padding: "12px", borderRadius: "12px", background: "#f0fdf4", border: "1px solid #bbf7d0", marginBottom: "12px" }}>
                        <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px", marginBottom: "4px" }}>
                          {(() => {
                            const qty = parseFloat(servingQuantity) || 1;
                            const label = `${servingAmount} ${servingUnit}`;
                            return qty === 1 ? `For ${label}:` : `For ${qty} × ${label}:`;
                          })()}
                        </div>
                        <div style={{ color: "#1e3a5f", fontSize: "14px" }}>
                          {preview.calories} kcal · P {preview.protein}g · C {preview.carbs}g · F {preview.fat}g
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ display: "flex", gap: "8px" }}>
                    <button type="button" onClick={handleAddFood} style={{ ...btnPrimary, flex: 1 }}>Add to {mealLabel}</button>
                    <button type="button" onClick={() => setSelectedFood(null)} style={btnSecondary}>Back</button>
                  </div>
                </div>
              )}

              {/* Close panel button */}
              {!selectedFood && (
                <button
                  type="button"
                  onClick={() => { setShowAddPanel(false); setSelectedFood(null); setSearchQuery(""); setSearchResults([]); }}
                  style={{ ...btnSecondary, width: "100%", marginTop: "12px" }}
                >
                  Cancel
                </button>
              )}
            </div>
          )}

          {foodLog.length === 0 && !showAddPanel && (
            <div style={{ ...cardStyle, textAlign: "center", color: "#4b6b8a" }}>
              <div style={{ fontSize: "36px", marginBottom: "8px" }}>🍽️</div>
              <div style={{ fontWeight: "bold", color: "#1e3a5f", marginBottom: "4px" }}>No foods logged yet</div>
              <div style={{ fontSize: "13px" }}>Tap + to search and log your first meal.</div>
            </div>
          )}

          {foodLog.length > 0 && MEAL_LABELS.map((meal) => {
            const entries = foodLog.filter((entry) => entry.meal === meal);
            if (!entries.length) return null;
            const mealTotals = entries.reduce(
              (acc, entry) => ({
                calories: acc.calories + (entry.calories || 0),
                protein: acc.protein + (entry.protein || 0),
                carbs: acc.carbs + (entry.carbs || 0),
                fat: acc.fat + (entry.fat || 0),
              }),
              { calories: 0, protein: 0, carbs: 0, fat: 0 }
            );
            return (
              <div key={meal} style={cardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                  <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>{meal}</div>
                  <div style={{ color: "#4b6b8a", fontSize: "12px" }}>
                    {mealTotals.calories} kcal · P {mealTotals.protein}g · C {mealTotals.carbs}g · F {mealTotals.fat}g
                  </div>
                </div>
                <div style={{ display: "grid", gap: "8px" }}>
                  {entries.map((entry) => (
                    <div
                      key={entry.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 12px",
                        borderRadius: "12px",
                        background: "#f0f9ff",
                        border: "1px solid #dbeafe",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {entry.name}
                        </div>
                        <div style={{ color: "#4b6b8a", fontSize: "12px", marginTop: "2px" }}>
                          {entry.calories} kcal · P {entry.protein}g · C {entry.carbs}g · F {entry.fat}g
                          {entry.servingGrams > 0 && ` · ${entry.servingGrams}g`}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDelete(entry.id)}
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "8px",
                          border: "none",
                          background: "#fecaca",
                          color: "#dc2626",
                          fontWeight: "bold",
                          fontSize: "14px",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}

      {view === "trends" && (
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
            <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px" }}>Calorie Trends</div>
            <div style={{ display: "flex", gap: "6px" }}>
              {[7, 14, 30].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => { setTrendsRange(r); loadTrends(r, date); }}
                  style={{
                    padding: "6px 12px", borderRadius: "10px", fontSize: "12px", fontWeight: "bold", cursor: "pointer",
                    border: trendsRange === r ? "2px solid #2563eb" : "2px solid #bfdbfe",
                    background: trendsRange === r ? "#e0f2fe" : "#f5faff",
                    color: "#1e3a5f",
                  }}
                >
                  {r}d
                </button>
              ))}
            </div>
          </div>

          {trendsLoading && (
            <div style={{ color: "#4b6b8a", fontSize: "13px", textAlign: "center", padding: "24px 0" }}>Loading trends...</div>
          )}

          {!trendsLoading && trendsData.length > 0 && (() => {
            const loggedDays = trendsData.filter((d) => d.logged);
            const hasAny = loggedDays.length > 0;
            const avgCal = hasAny ? Math.round(loggedDays.reduce((s, d) => s + d.calories, 0) / loggedDays.length) : 0;
            const avgProt = hasAny ? Math.round(loggedDays.reduce((s, d) => s + d.protein, 0) / loggedDays.length) : 0;
            const avgCarbs = hasAny ? Math.round(loggedDays.reduce((s, d) => s + d.carbs, 0) / loggedDays.length) : 0;
            const avgFat = hasAny ? Math.round(loggedDays.reduce((s, d) => s + d.fat, 0) / loggedDays.length) : 0;

            const chartData = trendsData.map((d) => ({
              label: d.date.slice(5).replace("-", "/"),
              calories: d.logged ? d.calories : null,
              protein: d.logged ? d.protein : null,
              carbs: d.logged ? d.carbs : null,
              fat: d.logged ? d.fat : null,
            }));

            return (
              <>
                {!hasAny ? (
                  <div style={{ color: "#4b6b8a", fontSize: "13px", textAlign: "center", padding: "24px 0" }}>
                    Log food on a few days to see your trends here.
                  </div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6b8cb0" }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "#6b8cb0" }} tickLine={false} axisLine={false} width={52} />
                        <Tooltip
                          contentStyle={{ borderRadius: "10px", border: "1px solid #bfdbfe", fontSize: "12px" }}
                          formatter={(val, name) => [val != null ? `${val}` : "—", name]}
                        />
                        {effectiveTargets.calories > 0 && (
                          <ReferenceLine y={effectiveTargets.calories} stroke="#93c5fd" strokeDasharray="4 4" />
                        )}
                        <Line type="monotone" dataKey="calories" stroke="#38bdf8" strokeWidth={2} dot={{ r: 3, fill: "#38bdf8" }} connectNulls={false} name="Calories" />
                      </LineChart>
                    </ResponsiveContainer>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "14px" }}>
                      {[
                        { label: "Avg Calories", val: avgCal, target: effectiveTargets.calories, unit: "kcal", color: "#38bdf8" },
                        { label: "Avg Protein", val: avgProt, target: effectiveTargets.protein, unit: "g", color: "#34d399" },
                        { label: "Avg Carbs", val: avgCarbs, target: effectiveTargets.carbs, unit: "g", color: "#fbbf24" },
                        { label: "Avg Fat", val: avgFat, target: effectiveTargets.fat, unit: "g", color: "#f87171" },
                      ].map(({ label, val, target, unit, color }) => (
                        <div key={label} style={{ padding: "10px 12px", borderRadius: "12px", background: "#f5faff", border: "1.5px solid #dbeafe" }}>
                          <div style={{ fontSize: "11px", color: "#6b8cb0", fontWeight: "bold", textTransform: "uppercase", marginBottom: "4px" }}>{label}</div>
                          <div style={{ fontSize: "16px", fontWeight: "bold", color }}>
                            {val}{unit === "kcal" ? "" : "g"}
                          </div>
                          {target > 0 && (
                            <div style={{ fontSize: "11px", color: "#9bb5cc" }}>target: {target}{unit === "kcal" ? "" : "g"}</div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: "11px", color: "#9bb5cc", marginTop: "10px", textAlign: "center" }}>
                      Averages based on {loggedDays.length} logged day{loggedDays.length !== 1 ? "s" : ""}
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}

      {view === "targets" && (
        <div style={cardStyle}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "18px", marginBottom: "4px" }}>
            Daily Macro Targets
          </div>
          <div style={{ color: "#4b6b8a", fontSize: "13px", marginBottom: "16px" }}>
            Set your daily goals for calories and macros.
          </div>
          <div style={{ display: "grid", gap: "12px" }}>
            {[
              { key: "calories", label: "Calories (kcal)", color: "#38bdf8" },
              { key: "protein", label: "Protein (g)", color: "#34d399" },
              { key: "carbs", label: "Carbs (g)", color: "#fbbf24" },
              { key: "fat", label: "Fat (g)", color: "#f87171" },
            ].map(({ key, label, color }) => (
              <div key={key}>
                <div style={{ color: "#4b6b8a", fontSize: "12px", fontWeight: "bold", marginBottom: "6px", textTransform: "uppercase" }}>
                  <span style={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "50%", background: color, marginRight: "6px" }} />
                  {label}
                </div>
                <input
                  type="number"
                  min="0"
                  value={editTargets[key]}
                  onChange={(event) => setEditTargets((prev) => ({ ...prev, [key]: event.target.value }))}
                  onFocus={(e) => e.target.select()}
                  style={inputStyle}
                />
              </div>
            ))}
          </div>
          <button type="button" onClick={handleSaveTargets} style={{ ...btnPrimary, width: "100%", marginTop: "16px" }}>
            Save Targets
          </button>

          <div style={{ marginTop: "20px", borderTop: "1px solid #dbeafe", paddingTop: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
              <div>
                <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>Override for {date}</div>
                <div style={{ color: "#4b6b8a", fontSize: "12px", marginTop: "2px" }}>
                  {effectiveTargets.isOverride ? "Custom targets active for this day" : "Using your base targets"}
                </div>
              </div>
              {effectiveTargets.isOverride && (
                <button
                  type="button"
                  onClick={handleClearOverride}
                  style={{ ...btnSecondary, padding: "6px 12px", fontSize: "13px", color: "#dc2626", border: "2px solid #fecaca", flexShrink: 0 }}
                >
                  Clear
                </button>
              )}
            </div>
            <div style={{ display: "grid", gap: "10px" }}>
              {[
                { key: "calories", label: "Calories (kcal)", color: "#38bdf8" },
                { key: "protein", label: "Protein (g)", color: "#34d399" },
                { key: "carbs", label: "Carbs (g)", color: "#fbbf24" },
                { key: "fat", label: "Fat (g)", color: "#f87171" },
              ].map(({ key, label, color }) => (
                <div key={key}>
                  <div style={{ color: "#4b6b8a", fontSize: "12px", fontWeight: "bold", marginBottom: "6px", textTransform: "uppercase" }}>
                    <span style={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "50%", background: color, marginRight: "6px" }} />
                    {label}
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={overrideDraft[key] !== undefined ? overrideDraft[key] : effectiveTargets[key]}
                    onChange={(e) => setOverrideDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                    onFocus={(e) => e.target.select()}
                    style={inputStyle}
                  />
                </div>
              ))}
            </div>
            <button type="button" onClick={handleSaveOverride} style={{ ...btnPrimary, width: "100%", marginTop: "12px", background: "#6366f1" }}>
              Save for {date} only
            </button>
          </div>
        </div>
      )}

      {/* Live barcode scanner overlay */}
      {showBarcodeScanner && (
        <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 2000, display: "flex", flexDirection: "column" }}>
          <video
            ref={videoRef}
            playsInline
            muted
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          {/* Targeting frame overlay */}
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px" }}>
            <div style={{ color: "#fff", fontSize: "15px", fontWeight: "bold", textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>
              Point at barcode
            </div>
            <div style={{ width: "260px", height: "110px", position: "relative" }}>
              {/* corner brackets */}
              {[["0,0","0,0"],["0,auto","auto,0"],["auto,0","0,auto"],["auto,auto","auto,auto"]].map(([tb, lr], i) => {
                const [t,b] = tb.split(","); const [l,r] = lr.split(",");
                return (
                  <div key={i} style={{ position: "absolute", top: t !== "auto" ? 0 : "auto", bottom: b !== "auto" ? 0 : "auto", left: l !== "auto" ? 0 : "auto", right: r !== "auto" ? 0 : "auto", width: "24px", height: "24px",
                    borderTop: t !== "auto" ? "3px solid #38bdf8" : "none",
                    borderBottom: b !== "auto" ? "3px solid #38bdf8" : "none",
                    borderLeft: l !== "auto" ? "3px solid #38bdf8" : "none",
                    borderRight: r !== "auto" ? "3px solid #38bdf8" : "none",
                  }} />
                );
              })}
            </div>
            <button
              type="button"
              onClick={closeBarcodeScanner}
              style={{ padding: "12px 28px", borderRadius: "14px", border: "2px solid rgba(255,255,255,0.5)", background: "rgba(0,0,0,0.5)", color: "#fff", fontWeight: "bold", fontSize: "15px", cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {customFoodPendingDelete && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "360px",
              background: "#ffffff",
              borderRadius: "18px",
              border: "2px solid #bfdbfe",
              padding: "18px",
              boxShadow: "0 20px 50px rgba(15, 23, 42, 0.18)",
            }}
          >
            <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "18px", marginBottom: "8px" }}>
              Delete custom food?
            </div>
            <div style={{ color: "#4b6b8a", fontSize: "14px", lineHeight: 1.5, marginBottom: "16px" }}>
              Are you sure you want to delete <strong>{customFoodPendingDelete.name}</strong>?
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={() => setCustomFoodPendingDelete(null)}
                style={{ ...btnSecondary, flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteCustomFood}
                style={{ ...btnPrimary, flex: 1, background: "#ef4444" }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
